import React from 'react';
import { isBlackjack } from '../utils/deck';
import { useGameStore } from '../store/gameStore';
import * as sounds from '../utils/sounds';
import { SwipeActionControls } from './common/SwipeActionControls';

export const MobileGameControls: React.FC = () => {
  const { 
    hit, 
    stand, 
    doubleDown, 
    split, 
    surrender,
    isPlaying, 
    roundComplete, 
    isDealing,
    playerHands,
    currentHandIndex,
    bankroll,
    settings,
    insuranceOffered
  } = useGameStore();

  const currentHand = playerHands[currentHandIndex];
  const hasBlackjackResult = currentHand?.result === 'blackjack';
  const hasNaturalBlackjack = currentHand &&
    currentHand.cards.length === 2 &&
    currentHand.cards.every(c => c.faceUp) &&
    isBlackjack(currentHand.cards);

  const canAct = isPlaying && !roundComplete && !isDealing && !insuranceOffered && !hasBlackjackResult && !hasNaturalBlackjack;

  const [showActions, setShowActions] = React.useState(false);
  const [actionsExiting, setActionsExiting] = React.useState(false);
  const shouldShowActions = canAct;

  React.useEffect(() => {
    if (shouldShowActions) {
      setActionsExiting(false);
      setShowActions(true);
    } else if (showActions) {
      setActionsExiting(true);
      const t = setTimeout(() => {
        setShowActions(false);
        setActionsExiting(false);
      }, 220);
      return () => clearTimeout(t);
    }
  }, [shouldShowActions, showActions]);

  const canSplit = canAct && currentHand?.cards?.length === 2 && 
    currentHand.bet <= bankroll &&
    (() => {
      if (!currentHand?.cards || currentHand.cards.length !== 2) return false;
      const [c1, c2] = currentHand.cards;
      const r1 = c1.rank === '10' || c1.rank === 'J' || c1.rank === 'Q' || c1.rank === 'K' ? '10' : c1.rank;
      const r2 = c2.rank === '10' || c2.rank === 'J' || c2.rank === 'Q' || c2.rank === 'K' ? '10' : c2.rank;
      return r1 === r2;
    })();

  const handleHit = () => {
    if (canAct) {
      sounds.playClick();
      hit();
    }
  };

  const handleStand = () => {
    if (canAct) {
      sounds.playClick();
      stand();
    }
  };

  const handleDouble = () => {
    if (canAct && currentHand?.cards?.length === 2 && currentHand.bet <= bankroll) {
      sounds.playClick();
      doubleDown();
    }
  };

  const handleSplit = () => {
    if (canSplit) {
      sounds.playClick();
      split();
    }
  };

  const handleSurrender = () => {
    if (canSurrender) {
      sounds.playClick();
      surrender();
    }
  };

  const allowSurrenderNow =
    (settings.surrenderAllowed && !insuranceOffered) ||
    (settings.earlySurrenderAllowed && insuranceOffered);

  const canSurrender = canAct &&
    currentHand?.cards?.length === 2 &&
    playerHands.length === 1 &&
    allowSurrenderNow;

  const canDouble = canAct && currentHand?.cards?.length === 2 && currentHand.bet <= bankroll;

  return (
    <SwipeActionControls
      onHit={handleHit}
      onStand={handleStand}
      onDouble={handleDouble}
      onSplit={handleSplit}
      onSurrender={canSurrender ? handleSurrender : undefined}
      canDouble={canDouble}
      canSplit={canSplit}
      canSurrender={canSurrender}
      show={showActions}
      usePortal
      wrapperClassName={actionsExiting ? 'fade-out-down' : 'fade-in-up'}
    />
  );
};

// Haptic feedback utility (for supported devices)
export function triggerHaptic(type: 'light' | 'medium' | 'heavy' = 'light') {
  if ('vibrate' in navigator) {
    const durations = { light: 10, medium: 20, heavy: 30 };
    navigator.vibrate(durations[type]);
  }
}
